import React, { useEffect, useState } from "react";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  Card,
  Grid,
  Typography,
  Button,
  DialogActions,
  TablePagination
} from "@mui/material";
import MoreMenu from "../Admin-PaymentMethod/MoreMenu.js";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import SearchNotFound from "../table/SearchNotFound";
import { applySortFilter } from "../../utils/tableFunctions";
import {
  getPendingPaymentBrands,
  getPaymentBrandWithActions,
  PendingPaymentBrandAction,
} from "../../services/payment";
import { Log } from "../../services/services.js";
import { useFormik } from "formik";
import { StyledTableRow } from "../TableRow.js";
import CircularLoader from "../circularLoader.js";
import FormDialog from "../FormDialog.js";
import CommonDialog from "../CustomDialog.js";
import PaymentBrandDetails from "./Dialogs/PaymentBrandDetails";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import * as yup from "yup";
import { styles } from "../../utils/styles";

const defaultValue = {
  is_active: true
}

const TABLE_HEAD = [
  { id: "name", label: "Payment Brands" },
  { id: "payment_method_list", label: "Payment Methods" },
  { id: "action", label: "Action" },
  { id: "details" }
];

function PaymentBrandPending({ userRole }) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [loaded, setLoaded] = useState(false);
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [pendingDetails, setPendingDetails] = useState(null);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [paymentBrandList, setPaymentBrandList] = useState({ results: [], count: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
  });

  const queryPage = (queryParams) => {
    setLoaded(false);
    getPendingPaymentBrands({
      setResponse: renderData,
      setLoaded: setLoaded,
      filter: { ...defaultValue, ...queryParams },
    });
    setPagination({
      page: queryParams.page || 1,
      page_size: queryParams.page_size || 50,
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: parseInt(event.target.value, 10),
    });
  };

  const [selectedId, setSelectedId] = useState();
  const handleCloseDialog = (shouldResetDetails) => {
    setOpenRejectDialog(false);
    if (shouldResetDetails === true) setPendingDetails(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredList = applySortFilter(
    paymentBrandList.results || [],
    order,
    orderBy
  );

  const isItemNotFound = (paymentBrandList.results || []).length === 0;

  const renderData = data => {
    if (!data || !data.results) return;
    const newData = data.results.map(item => ({
      ...item,
      payment_method_list: item.payment_method
        ?.map((method) => method.name)
        .join(", ")
    }))
    setPaymentBrandList({ count: data.count, results: newData });
  }

  useEffect(() => {
    queryPage(pagination);
  }, []);

  const RejectPendingBrandDialog = ({
    open,
    onClose,
    selectedId,
    setPaymentBrandList,
    setLoaded,
  }) => {
    const {
      handleChange,
      handleSubmit,
      handleBlur,
      values,
      touched,
      errors,
      isSubmitting,
      resetForm
    } = useFormik({
      initialValues: {
        reason: "",
      },
      validationSchema: yup.object().shape({
        reason: yup.string().required("Required").trim(),
      }),
      onSubmit: async (values) => {
        Log.info(`Click button [Reject] - Reject Payment brand by Id: ${selectedId}`, values);
        await PendingPaymentBrandAction(selectedId, "reject", setPaymentBrandList, setLoaded, values.reason);
        handleClose(true);
      }
    });

    const handleClose = (isSubmitted) => {
      onClose(isSubmitted);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Reject this payment brand?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <Grid container sx={{ alignItems: "center" }} spacing={3}>
          <Grid item md={5} xs={5}>
            <Typography variant="subtitle1">Reason *</Typography>
          </Grid>
          <Grid item md={7} xs={7}>
            <TextField
              name="reason"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              required
              placeholder="Please enter your reason"
              error={Boolean(touched.reason && errors.reason)}
              helperText={touched.reason && errors.reason}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </FormDialog>
    );
  };

  return (
    <Box>
      {/* <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      /> */}
      {loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map(({ id, name, payment_method_list, action, is_active }) => {
                    return (
                      <StyledTableRow
                        hover
                        key={id}
                        sx={{ cursor: "default" }}
                      >
                        <TableCell children={name} id={id} />
                        <TableCell
                          children={payment_method_list}
                          id={id}
                        />
                        <TableCell children={action} id={id} />
                        <TableCell>
                          <Button
                            variant="text"
                            color="secondary"
                            onClick={() => {
                              setGlobalLoading(true);
                              getPaymentBrandWithActions(id, setPendingDetails, () => setGlobalLoading(false)
                              );
                            }}
                          >
                            View Detail
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={paymentBrandList?.count || 0}
            rowsPerPage={pagination.page_size}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          <RejectPendingBrandDialog
            open={openRejectDialog}
            onClose={handleCloseDialog}
            selectedId={selectedId}
            setPaymentBrandList={renderData}
            setLoaded={setLoaded}
          />
        </>
      )}

      {pendingDetails && (
        <CommonDialog
          title="Payment Brand Details"
          onClose={() => setPendingDetails(null)}
          renderActions={() =>
            userRole.includes("Approve/Reject") && (
              <DialogActions>
                <Button
                  variant="outlined"
                  // color="inherit"
                  onClick={() => {
                    setOpenRejectDialog(true);
                    setSelectedId(pendingDetails.id);
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setGlobalLoading(true);
                    Log.info(
                      `Click button [Approve] - Approve Payment Brand by Id: ${pendingDetails.id}`
                    );
                    await PendingPaymentBrandAction(
                      pendingDetails.id,
                      "approve",
                      renderData,
                      setLoaded
                    );
                    setPendingDetails(null);
                    setGlobalLoading(false);
                  }}
                  sx={{ ...styles.customButton }}
                >
                  Approve
                </Button>
              </DialogActions>
            )
          }
        >
          <PaymentBrandDetails detailsData={pendingDetails} />
        </CommonDialog>
      )}
    </Box>
  );
}

export default PaymentBrandPending;
